import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import PageShell from "@/components/site/PageShell";
import { useActivitiesContent, useActivityList } from "@/data/activitiesContent";

// Inline markdown bold (**text**) → <strong>
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={i} className="text-[#1a365d] font-semibold">
          {p.slice(2, -2)}
        </strong>
      );
    }
    if (p.startsWith("*") && p.endsWith("*")) {
      return (
        <em key={i} className="italic">
          {p.slice(1, -1)}
        </em>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

function renderParagraphs(text) {
  return text.split(/\n\n+/).map((para, idx) => (
    <p key={idx} className={idx === 0 ? "" : "mt-4"}>
      {renderInline(para)}
    </p>
  ));
}

export default function ActivityDetail() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const content = useActivitiesContent();
  const list = useActivityList();
  const c = content[slug];

  if (!c) {
    return <Navigate to="/activities" replace />;
  }

  // Find next activity for "continue reading" link
  const currentIdx = list.findIndex((a) => a.slug === slug);
  const nextActivity = list[(currentIdx + 1) % list.length];

  return (
    <PageShell>
      {/* Hero */}
      <section className="bg-[#fafaf7] border-b border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-16">
          <Link
            to="/activities"
            data-testid="activity-back-link"
            className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.28em] uppercase text-[#475569] hover:text-[#1a365d]"
          >
            <ArrowLeft size={12} /> {t("nav.activities")}
          </Link>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7 reveal">
              <span className="eyebrow">— {c.eyebrow}</span>
              <h1
                data-testid={`activity-title-${slug}`}
                className="font-display text-5xl md:text-7xl text-[#1a365d] mt-4 leading-[1.02] tracking-tight"
              >
                {c.title} <span className="italic">{c.italic}</span>.
              </h1>
            </div>
            <div className="lg:col-span-5 reveal delay-1">
              <img
                src={c.heroImage}
                alt={c.title}
                loading="eager"
                className="w-full h-auto border border-[#e2e8f0] shadow-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="font-display text-2xl md:text-[28px] text-[#1a365d] leading-[1.4] reveal space-y-6">
            {c.intro.map((p, i) => (
              <div key={i}>{renderInline(p)}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Blocks */}
      <section className="bg-[#fafaf7] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-20 md:space-y-28">
          {c.blocks.map((b, i) => {
            const reverse = i % 2 === 1;
            return (
              <div
                key={i}
                data-testid={`activity-block-${slug}-${i}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center ${
                  reverse ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-5 reveal">
                  <div className="zoom-wrap aspect-square w-full max-w-[360px] mx-auto lg:mx-0 lg:ml-auto bg-[#fafaf7] border border-[#e2e8f0]">
                    <img
                      src={b.image}
                      alt={b.title}
                      loading="lazy"
                      className={`w-full h-full ${
                        b.imageFit === "contain"
                          ? "object-contain p-6 bg-white"
                          : "object-cover"
                      }`}
                    />
                  </div>
                </div>
                <div className="lg:col-span-7 reveal delay-1">
                  {b.tag && (
                    <span className="text-[10.5px] tracking-[0.28em] uppercase text-[#d4af37] font-medium">
                      — {b.tag}
                    </span>
                  )}
                  <h3 className="font-display text-3xl md:text-4xl text-[#1a365d] mt-4 leading-[1.12]">
                    {b.title}
                  </h3>
                  <div className="mt-5 text-[#475569] leading-[1.85] text-base md:text-[16.5px]">
                    {renderParagraphs(b.text)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Next activity */}
      <section className="bg-white py-20 md:py-24 border-t border-[#e2e8f0]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 reveal">
          <span className="eyebrow">— {t("common.discover")}</span>
          <Link
            to={`/activities/${nextActivity.slug}`}
            data-testid="activity-next-link"
            className="mt-6 group flex items-center justify-between gap-6 border-y border-[#e2e8f0] py-8 hover:bg-[#fafaf7] -mx-6 md:-mx-10 px-6 md:px-10 transition-colors"
          >
            <div>
              <div className="text-[10.5px] tracking-[0.28em] uppercase text-[#475569]">
                {nextActivity.eyebrow}
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-[#1a365d] mt-2 group-hover:text-[#d4af37] transition-colors">
                {nextActivity.title}
              </h3>
            </div>
            <ArrowRight
              size={28}
              strokeWidth={1.4}
              className="text-[#1a365d] group-hover:text-[#d4af37] transition-colors shrink-0"
            />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
